import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Eyebrow } from "@/components/Eyebrow";
import { BookButton } from "@/components/BookButton";
import { LiveNumber } from "@/components/AnimatedNumber";
import { BLENDED_HOURLY_RATE, CALC_HIGH_THRESHOLD, CALC_MID_THRESHOLD } from "@/lib/site";

const REFRAME_LINES = {
    high: "Your AI team on retainer starts at a fraction of this. And it never calls in sick.",
    mid: "A retainer costs less than this grind. Every single month.",
    low: "Honest answer: at your size, a retainer might not pay for itself yet. A one time build probably will. That is the real math, and it is what the call is for.",
};

export function MathSection() {
    const [people, setPeople] = useState(8);
    const [hours, setHours] = useState(6);

    const { hoursLost, cost } = useMemo(() => {
        const weeklyHours = people * hours;
        const monthlyHours = weeklyHours * 4.33;
        return { hoursLost: monthlyHours, cost: monthlyHours * BLENDED_HOURLY_RATE };
    }, [people, hours]);

    const reframeState = cost >= CALC_HIGH_THRESHOLD ? "high" : cost > CALC_MID_THRESHOLD ? "mid" : "low";

    return (
        <section id="math" data-testid="math" className="py-20 sm:py-24">
            <div className="max-w-6xl mx-auto px-5 sm:px-8">
                <Eyebrow>The math</Eyebrow>
                <h2 className="mt-5 max-w-3xl font-display font-semibold tracking-[-0.02em] text-4xl sm:text-5xl leading-[1.08]">
                    Find out what the grind is costing you<span className="period">.</span>
                </h2>

                <div className="mt-12 rounded-3xl border border-border bg-surface p-7 sm:p-12">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        {/* sliders */}
                        <div className="space-y-12">
                            <div>
                                <div className="flex items-baseline justify-between mb-5">
                                    <label className="text-sm text-muted-foreground">People on your team</label>
                                    <span data-testid="people-value" className="font-display font-semibold text-2xl tabular-nums">
                                        {people}
                                    </span>
                                </div>
                                <Slider
                                    data-testid="people-slider"
                                    value={[people]}
                                    onValueChange={(v) => setPeople(v[0])}
                                    min={2}
                                    max={50}
                                    step={1}
                                />
                            </div>

                            <div>
                                <div className="flex items-baseline justify-between mb-5">
                                    <label className="text-sm text-muted-foreground">
                                        Hours per person per week on manual admin
                                    </label>
                                    <span data-testid="hours-value" className="font-display font-semibold text-2xl tabular-nums">
                                        {hours}
                                    </span>
                                </div>
                                <Slider
                                    data-testid="hours-slider"
                                    value={[hours]}
                                    onValueChange={(v) => setHours(v[0])}
                                    min={2}
                                    max={15}
                                    step={1}
                                />
                            </div>
                        </div>

                        {/* live output */}
                        <div className="lg:border-l lg:border-border lg:pl-16 space-y-10">
                            <div>
                                <p className="text-sm text-muted-foreground mb-2">Estimated hours lost per month</p>
                                <p data-testid="hours-lost" className="font-display font-bold tracking-tight text-6xl sm:text-7xl tabular-nums leading-none">
                                    <LiveNumber value={hoursLost} />
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground mb-2">Estimated monthly cost</p>
                                <p data-testid="monthly-cost" className="font-display font-bold tracking-tight text-6xl sm:text-7xl tabular-nums leading-none">
                                    $<LiveNumber value={cost} />
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 pt-8 border-t border-border">
                        <div className="max-w-2xl min-h-[3.5rem]">
                            <AnimatePresence mode="wait">
                                <motion.p
                                    key={reframeState}
                                    data-testid="calc-reframe-line"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="text-lg sm:text-xl text-foreground/90 leading-relaxed"
                                >
                                    {REFRAME_LINES[reframeState]}
                                </motion.p>
                            </AnimatePresence>
                        </div>
                        <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
                            <BookButton testid="math-book-a-call" />
                            <span className="text-sm text-muted-foreground">Free 30-minute call, no pitch.</span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
